import { SelectProps } from '@amzn/awsui-components-core';
import { createSelector } from '@reduxjs/toolkit';
import { FIRST_GENERATION_LABEL } from '../../constants/appConstants';
import { ProductLineDetail, ProductLineWithRpns, RpnListItem } from '../../types/productLines/productHierarchy.types';
import * as permissionsUtils from '../../utils/permissionsUtils';
import { createThreeTierSortComparator, sortProductLineOptions } from '../../utils/productLineOptionsUtils';
import { RootState } from '../store';

// ============================================================================
// Section 1: Full Product Hierarchy Selectors (state.productHierarchy.productHierarchy)
//
// These selectors access the full (costly) product hierarchy data.
// The full hierarchy includes enriched metadata fields (id, hierarchy_level, hierarchy_name)
// needed for Ag-Grid tree data in the BulkEdit component.
//
// IMPORTANT: Only the BulkEdit component should depend on this data.
// All other components should use the Minimal Hierarchy selectors below.
// ============================================================================

/** Selects the entire productHierarchy state object (data, isLoading, error, etc.) */
export const selectProductHierarchy = (state: RootState) => state.productHierarchy.productHierarchy;

/** Selects the full product hierarchy data array (ProductLineWithMeta[]) */
export const selectProductHierarchyData = (state: RootState) => state.productHierarchy.productHierarchy.data;

/** Selects whether the full product hierarchy is currently loading */
export const selectProductHierarchyLoading = (state: RootState) => state.productHierarchy.productHierarchy.isLoading;

/** Selects the error message from the full product hierarchy fetch, or null */
export const selectProductHierarchyError = (state: RootState) => state.productHierarchy.productHierarchy.error;

/** Selects whether a fetch for the full product hierarchy has been attempted */
export const selectProductHierarchyHasAttemptedFetch = (state: RootState) =>
  state.productHierarchy.productHierarchy.hasAttemptedFetch;

/** Selects whether the full product hierarchy has been successfully fetched */
export const selectProductHierarchyHasSuccessfulFetch = (state: RootState) =>
  state.productHierarchy.productHierarchy.hasSuccessfulFetch;

/** Selects the timestamp of the last successful full product hierarchy fetch */
export const selectProductHierarchyLastFetchTimestamp = (state: RootState) =>
  state.productHierarchy.productHierarchy.lastFetchTimestamp;

/**
 * Filters confidential RPNs from the full product hierarchy based on user permissions.
 * Uses the confidential map from the minimal hierarchy for permission checks.
 *
 * - Tech Admin / Verity Admin: See everything (no filtering)
 * - Extended Access PL with has_confidential_access: See confidential RPNs for allowed PLs
 * - Read Only / users without confidential access: Confidential RPNs are filtered out
 *
 * Used in BulkEditHomePage, ProductHierarchyTable, and sidebar navigation
 * to ensure users only see RPNs they have access to.
 */
export const selectFilteredProductHierarchy = createSelector(
  [
    selectProductHierarchyData,
    (state: RootState) => state.accessManagement.currentUserPermissions.data,
    (state: RootState) => state.productHierarchy.productHierarchyMinimal.data,
  ],
  (hierarchyData, userPermission, minimalData) => {
    // Always filter out inactive, invalid, or placeholder product lines
    const validData = hierarchyData.filter((pl) => pl.is_active && pl.product_code && pl.product_line_id != -1);

    // Build confidential map from minimal data inline
    const rpnConfidentialMap: Record<number, boolean> = {};
    minimalData.forEach((productLine) => {
      productLine.children?.forEach((rpn) => {
        rpnConfidentialMap[rpn.rpn_id] = rpn.is_confidential;
      });
    });

    // Tech Admin and Verity Admin see everything (but still respect active/valid filter above)
    if (permissionsUtils.isTechAdmin(userPermission) || permissionsUtils.isVerityAdmin(userPermission)) {
      return validData;
    }

    // For other users, filter out confidential RPNs they can't access
    const filtered = validData.map((productLine) => ({
      ...productLine,
      children: productLine.children.filter((rpn) => {
        const isConfidential = rpnConfidentialMap[rpn.rpn_id] || false;
        const hasAccess = permissionsUtils.hasConfidentialAccessForProductLine(
          userPermission,
          productLine.product_line_id
        );

        // Non-confidential RPNs are always visible
        if (!isConfidential) return true;
        // Confidential RPNs: only visible if user has confidential access for this PL
        return hasAccess;
      }),
    }));

    return filtered;
  }
);

// ============================================================================
// Section 2: Minimal Product Hierarchy Selectors (state.productHierarchy.productHierarchyMinimal)
//
// These selectors access the lightweight minimal hierarchy data.
// The minimal hierarchy contains only essential identification fields and is
// fetched at app initialization for fast performance.
//
// Use these selectors for: permissions, navigation, form dropdowns,
// Home page widgets, and any component that does NOT need the full Ag-Grid metadata.
// ============================================================================

// --- Base minimal hierarchy selectors ---

/** Selects the entire productHierarchyMinimal state object (data, isLoading, error, etc.) */
export const selectProductHierarchyMinimalState = (state: RootState) => state.productHierarchy.productHierarchyMinimal;

/** Selects the minimal product hierarchy data array (ProductHierarchyMinimalDetails[]) */
export const selectProductHierarchyMinimalData = (state: RootState) =>
  state.productHierarchy.productHierarchyMinimal.data;

/** Selects whether the minimal product hierarchy is currently loading */
export const selectProductHierarchyMinimalLoading = (state: RootState) =>
  state.productHierarchy.productHierarchyMinimal.isLoading;

/** Selects the error message from the minimal product hierarchy fetch, or null */
export const selectProductHierarchyMinimalError = (state: RootState) =>
  state.productHierarchy.productHierarchyMinimal.error;

/** Selects whether a fetch for the minimal product hierarchy has been attempted */
export const selectProductHierarchyMinimalHasAttemptedFetch = (state: RootState) =>
  state.productHierarchy.productHierarchyMinimal.hasAttemptedFetch;

/** Selects whether the minimal product hierarchy has been successfully fetched */
export const selectProductHierarchyMinimalHasSuccessfulFetch = (state: RootState) =>
  state.productHierarchy.productHierarchyMinimal.hasSuccessfulFetch;

/** Selects the timestamp of the last successful minimal product hierarchy fetch */
export const selectProductHierarchyMinimalLastFetchTimestamp = (state: RootState) =>
  state.productHierarchy.productHierarchyMinimal.lastFetchTimestamp;

// --- Derived minimal hierarchy selectors ---

/**
 * Derives a flat/full (active / Inactive) product lines list from the minimal hierarchy.
 * Extracts product line identification fields without nested children.
 * Replaces the old dedicated productLines state slice.
 */
export const selectProductLinesDataFromMinimalHierarchy = createSelector(
  [selectProductHierarchyMinimalData],
  (minimalData) => {
    return minimalData
      .filter((pl) => pl.is_active && pl.product_code && pl.product_line_id != -1)
      .map((pl) => ({
        product_line_id: pl.product_line_id,
        product_code: pl.product_code,
        product_line: pl.product_line,
        is_active: pl.is_active,
      }));
  }
);

/**
 * Builds a map of rpn_id → is_confidential from the minimal hierarchy.
 * Used for permission checks to determine RPN visibility without loading the full hierarchy.
 */
export const selectRpnConfidentialMapFromMinimalHierarchy = createSelector(
  [selectProductHierarchyMinimalData],
  (minimalData): Record<number, boolean> => {
    const confidentialMap: Record<number, boolean> = {};

    minimalData.forEach((productLine) => {
      productLine.children?.forEach((rpn) => {
        confidentialMap[rpn.rpn_id] = rpn.is_confidential;
      });
    });

    return confidentialMap;
  }
);

/**
 * Selects active product lines formatted as Cloudscape Select options.
 * Each option includes the product line ID as value, name as label, and product code as description.
 * Options are sorted using the three-tier sorting logic:
 * 1. Primary sort: Device suffix first, Accessories last
 * 2. Secondary sort: Options with 0 RPNs go to the bottom (when labelTag is present)
 * 3. Tertiary sort: Alphabetical by label
 */
export const selectActiveProductLineOptionsFromMinimalHierarchy = createSelector(
  [selectProductLinesDataFromMinimalHierarchy],
  (productLinesData) => {
    const options = productLinesData
      .filter((pl) => pl.is_active && pl.product_code && pl.product_line_id != -1)
      .map(
        (pl): SelectProps.Option => ({
          value: `${pl.product_line_id}`,
          label: pl.product_line,
          description: pl.product_code,
        })
      );

    return sortProductLineOptions(options);
  }
);

/**
 * Selects active product line options enriched with RPN count as labelTag.
 * Counts RPNs per product line from the minimal hierarchy children.
 * The labelTag is displayed as a tag next to the option label in Select/Multiselect dropdowns.
 * Options are sorted using the three-tier sorting logic:
 * 1. Primary sort: Device suffix first, Accessories last
 * 2. Secondary sort: Options with 0 RPNs go to the bottom (when labelTag is present)
 * 3. Tertiary sort: Alphabetical by label
 */
export const selectActiveProductLineOptionsWithRpnCountFromMinimalHierarchy = createSelector(
  [selectProductLinesDataFromMinimalHierarchy, selectProductHierarchyMinimalData],
  (productLinesData, minimalData) => {
    const rpnCountMap = new Map<number, number>();
    minimalData.forEach((pl) => {
      rpnCountMap.set(pl.product_line_id, pl.children?.length || 0);
    });

    const options = productLinesData
      .filter((pl) => pl.is_active && pl.product_code && pl.product_line_id != -1)
      .map(
        (pl): SelectProps.Option => ({
          value: `${pl.product_line_id}`,
          label: pl.product_line,
          description: pl.product_code,
          labelTag: `${rpnCountMap.get(pl.product_line_id) || 0} RPNs`,
        })
      );

    return sortProductLineOptions(options);
  }
);

/**
 * Selects active product line options grouped by product_line_group from backend.
 * Within each group, options are sorted using the three-tier sorting logic:
 * 1. Primary sort: Device suffix first, Accessories last
 * 2. Secondary sort: Options with 0 RPNs go to the bottom (when labelTag is present)
 * 3. Tertiary sort: Alphabetical by label
 */
export const selectGroupedProductLineOptionsFromMinimalHierarchy = createSelector(
  [selectActiveProductLineOptionsFromMinimalHierarchy, selectProductHierarchyMinimalData],
  (options, minimalData): SelectProps.OptionGroup[] => {
    // Create a map of product_line_id to product_line_group
    const productLineGroupMap = new Map<number, string>();
    minimalData.forEach((pl) => {
      productLineGroupMap.set(pl.product_line_id, pl.product_line_group || 'Other');
    });

    // Group options by product_line_group
    const groups = new Map<string, SelectProps.Option[]>();
    options.forEach((option) => {
      const productLineId = parseInt(option.value || '0');
      const groupName = productLineGroupMap.get(productLineId) || 'Other';

      if (!groups.has(groupName)) {
        groups.set(groupName, []);
      }
      groups.get(groupName)?.push(option);
    });

    // Convert to OptionGroup array, sort groups alphabetically, and sort options within each group
    return Array.from(groups.entries())
      .sort(([keyA], [keyB]) => keyA.localeCompare(keyB))
      .map(([groupKey, groupOptions]) => ({
        label: groupKey,
        options: sortProductLineOptions(groupOptions),
      }));
  }
);

/**
 * Selects active product line options with RPN count, grouped by product_line_group from backend.
 * Within each group, options are sorted using the three-tier sorting logic:
 * 1. Primary sort: Device suffix first, Accessories last
 * 2. Secondary sort: Options with 0 RPNs go to the bottom (when labelTag is present)
 * 3. Tertiary sort: Alphabetical by label
 */
export const selectGroupedProductLineOptionsWithRpnCountFromMinimalHierarchy = createSelector(
  [selectActiveProductLineOptionsWithRpnCountFromMinimalHierarchy, selectProductHierarchyMinimalData],
  (options, minimalData): SelectProps.OptionGroup[] => {
    // Create a map of product_line_id to product_line_group
    const productLineGroupMap = new Map<number, string>();
    minimalData.forEach((pl) => {
      productLineGroupMap.set(pl.product_line_id, pl.product_line_group || 'Other');
    });

    // Group options by product_line_group
    const groups = new Map<string, SelectProps.Option[]>();
    options.forEach((option) => {
      const productLineId = parseInt(option.value || '0');
      const groupName = productLineGroupMap.get(productLineId) || 'Other';

      if (!groups.has(groupName)) {
        groups.set(groupName, []);
      }
      groups.get(groupName)?.push(option);
    });

    // Convert to OptionGroup array, sort groups alphabetically, and sort options within each group
    return Array.from(groups.entries())
      .sort(([keyA], [keyB]) => keyA.localeCompare(keyB))
      .map(([groupKey, groupOptions]) => ({
        label: groupKey,
        options: sortProductLineOptions(groupOptions),
      }));
  }
);

/**
 * Selects a specific product line by its numeric ID from the minimal hierarchy.
 * Returns undefined if no matching product line is found.
 *
 * @param state - Redux state
 * @param id - Product line ID to look up
 */
export const selectProductLineByIdFromMinimalHierarchy = createSelector(
  [selectProductLinesDataFromMinimalHierarchy, (_state: RootState, id: number) => id],
  (productLines, id) => {
    return productLines.find((pl) => pl.product_line_id === id);
  }
);

/**
 * Selects product lines with their RPN children from the minimal hierarchy.
 * Returns a simplified data structure (ProductLineWithRpns[]) for form usage
 * such as duplicate name validation and RPN selection dropdowns.
 */
export const selectProductLinesWithRpns = createSelector(
  [selectProductHierarchyMinimalData],
  (minimalData): ProductLineWithRpns[] => {
    return minimalData.map((pl) => ({
      product_line_id: pl.product_line_id,
      product_line: pl.product_line,
      product_code: pl.product_code,
      rpns: (pl.children || []).map((rpn) => ({
        rpn_id: rpn.rpn_id,
        rpn_alias: rpn.rpn_alias,
      })),
    }));
  }
);

/**
 * Selects RPNs for a specific product line by ID from the minimal hierarchy.
 * Useful for duplicate name validation in CreateRpn form.
 *
 * @param state - Redux state
 * @param product_line_id - Product line ID to get RPNs for
 */
export const selectRpnsByProductLineId = createSelector(
  [selectProductLinesWithRpns, (_state: RootState, product_line_id: number) => product_line_id],
  (productLinesWithRpns, product_line_id): RpnListItem[] => {
    const productLine = productLinesWithRpns.find((pl) => pl.product_line_id === product_line_id);
    return productLine?.rpns || [];
  }
);

/**
 * Selects FPNs for a specific RPN within a product line from the minimal hierarchy.
 * Returns array of {id, name} objects for FPN name matching.
 * Used for FPN ID lookup in Add SKU modal.
 *
 * @param state - Redux state
 * @param productLineId - Product line ID containing the RPN
 * @param rpnId - RPN ID to get FPNs for
 */
export const selectFpnsByRpnId = createSelector(
  [
    selectProductHierarchyMinimalData,
    (_state: RootState, productLineId: number) => productLineId,
    (_state: RootState, _productLineId: number, rpnId: number) => rpnId,
  ],
  (
    minimalData,
    productLineId,
    rpnId
  ): {
    id: number;
    name: string;
  }[] => {
    // Find the product line
    const productLine = minimalData.find((pl) => pl.product_line_id === productLineId);
    if (!productLine) return [];

    // Find the RPN within the product line
    const rpn = productLine.children?.find((r) => r.rpn_id === rpnId);
    if (!rpn || !rpn.children) return [];

    // Map FPN children to {id, name} format
    return rpn.children.map((fpn) => ({
      id: fpn.fpn_id,
      name: fpn.fpn_alias,
    }));
  }
);

/**
 * Selects an RPN's numeric ID by its display ID within a specific product line from the minimal hierarchy.
 * Returns the numeric rpn_id if found, null otherwise.
 *
 * @param state - Redux state
 * @param productLineId - Product line ID to search within
 * @param rpnDisplayId - RPN display ID (e.g., 'R11243') to find
 */
export const selectRpnIdByDisplayId = createSelector(
  [
    selectProductHierarchyMinimalData,
    (_state: RootState, productLineId: number) => productLineId,
    (_state: RootState, _productLineId: number, rpnDisplayId: string) => rpnDisplayId,
  ],
  (minimalData, productLineId, rpnDisplayId): number | null => {
    // Find the product line
    const productLine = minimalData.find((pl) => pl.product_line_id === productLineId);
    if (!productLine || !productLine.children) return null;

    // Find the RPN with matching display ID
    const rpn = productLine.children.find((r) => r.rpn_display_id === rpnDisplayId);
    return rpn ? rpn.rpn_id : null;
  }
);

/**
 * Selects an FPN's numeric ID by its display ID within a specific RPN from the minimal hierarchy.
 * Returns the numeric fpn_id if found, null otherwise.
 *
 * @param state - Redux state
 * @param productLineId - Product line ID containing the RPN
 * @param rpnId - RPN ID containing the FPN
 * @param fpnDisplayId - FPN display ID (e.g., 'F010652') to find
 */
export const selectFpnIdByDisplayId = createSelector(
  [
    selectProductHierarchyMinimalData,
    (_state: RootState, productLineId: number) => productLineId,
    (_state: RootState, _productLineId: number, rpnId: number) => rpnId,
    (_state: RootState, _productLineId: number, _rpnId: number, fpnDisplayId: string) => fpnDisplayId,
  ],
  (minimalData, productLineId, rpnId, fpnDisplayId): number | null => {
    // Find the product line
    const productLine = minimalData.find((pl) => pl.product_line_id === productLineId);
    if (!productLine || !productLine.children) return null;

    // Find the RPN within the product line
    const rpn = productLine.children.find((r) => r.rpn_id === rpnId);
    if (!rpn || !rpn.children) return null;

    // Find the FPN with matching display ID
    const fpn = rpn.children.find((f) => f.fpn_display_id === fpnDisplayId);
    return fpn ? fpn.fpn_id : null;
  }
);

// ============================================================================
// Section 3: Mandatory Attributes Selectors (state.productHierarchy.mandatoryAttributes)
//
// These selectors access the mandatory attributes configuration data.
// Mandatory attributes specify which fields are required for specific product lines.
// Used for form validation and UI indication of required fields.
// ============================================================================

/** Selects the entire mandatoryAttributes state object (data, isLoading, error, etc.) */
export const selectMandatoryAttributesState = (state: RootState) => state.productHierarchy.mandatoryAttributes;

/** Selects the mandatory attributes data array (MandatoryAttribute[]) */
export const selectMandatoryAttributesData = (state: RootState) => state.productHierarchy.mandatoryAttributes.data;

/** Selects whether the mandatory attributes are currently loading */
export const selectMandatoryAttributesLoading = (state: RootState) =>
  state.productHierarchy.mandatoryAttributes.isLoading;

/** Selects the error message from the mandatory attributes fetch, or null */
export const selectMandatoryAttributesError = (state: RootState) => state.productHierarchy.mandatoryAttributes.error;

/** Selects whether a fetch for mandatory attributes has been attempted */
export const selectMandatoryAttributesHasAttemptedFetch = (state: RootState) =>
  state.productHierarchy.mandatoryAttributes.hasAttemptedFetch;

/** Selects whether mandatory attributes have been successfully fetched */
export const selectMandatoryAttributesHasSuccessfulFetch = (state: RootState) =>
  state.productHierarchy.mandatoryAttributes.hasSuccessfulFetch;

/**
 * Selects mandatory attribute labels for a specific product line.
 * Returns an array of uppercase attribute labels that are mandatory for the given product line.
 *
 * @param state - Redux state
 * @param productLineId - Product line ID to get mandatory attributes for
 *
 * @example
 * ```typescript
 * const mandatoryLabels = useAppSelector((state) =>
 *   selectMandatoryAttributesByProductLine(state, 123)
 * );
 * // Returns ['COUNTRY', 'PLATFORM'] if those are mandatory for product line 123
 * ```
 */
export const selectMandatoryAttributesByProductLine = createSelector(
  [selectMandatoryAttributesData, (_state: RootState, productLineId: number) => productLineId],
  (mandatoryAttributes, productLineId): string[] => {
    return mandatoryAttributes
      .filter((rule) => rule.product_line_id === productLineId)
      .map((rule) => rule.attribute_type_label.toUpperCase());
  }
);

/**
 * Selects program predecessor options for a specific product line as Cloudscape Select options.
 * Returns FIRST_GENERATION_LABEL as the first option followed by sorted sibling RPN aliases
 * (all RPNs within the same product line).
 *
 * Used by EditableOverviewSection for the Program Predecessor dropdown.
 * The calling component is responsible for filtering out the current RPN's own alias if needed.
 *
 * @param state - Redux state
 * @param productLineId - Product line ID to get sibling RPN aliases for
 */
export const selectProgramPredecessorOptionsByProductLineId = createSelector(
  [selectProductHierarchyMinimalData, (_state: RootState, productLineId: number) => productLineId],
  (minimalData, productLineId): SelectProps.Option[] => {
    const productLine = minimalData.find((pl) => pl.product_line_id === productLineId);
    if (!productLine || !productLine.children) {
      return [{ label: FIRST_GENERATION_LABEL, value: FIRST_GENERATION_LABEL }];
    }

    const siblingAliases = productLine.children
      .map((rpn) => rpn.rpn_alias)
      .filter(Boolean)
      .sort((a, b) => a.localeCompare(b));

    return [
      { label: FIRST_GENERATION_LABEL, value: FIRST_GENERATION_LABEL },
      ...siblingAliases.map((alias) => ({ label: alias, value: alias })),
    ];
  }
);

/**
 * Checks if a specific attribute is mandatory for a given product line.
 * Returns true if the attribute is required, false otherwise.
 *
 * @param state - Redux state
 * @param attributeLabel - The attribute label to check (case-insensitive)
 * @param productLineId - Product line ID to check against
 *
 * @example
 * ```typescript
 * const isMandatory = useAppSelector((state) =>
 *   selectIsAttributeMandatory(state, 'COUNTRY', 123)
 * );
 * // Returns true if COUNTRY is mandatory for product line 123
 * ```
 */
export const selectIsAttributeMandatory = createSelector(
  [
    selectMandatoryAttributesData,
    (_state: RootState, attributeLabel: string) => attributeLabel,
    (_state: RootState, _attributeLabel: string, productLineId: number) => productLineId,
  ],
  (mandatoryAttributes, attributeLabel, productLineId): boolean => {
    return mandatoryAttributes.some(
      (rule) =>
        rule.attribute_type_label.toUpperCase() === attributeLabel.toUpperCase() &&
        rule.product_line_id === productLineId
    );
  }
);

// ============================================================================
// Section 4: Program Predecessor Conversion Utilities
//
// These selectors help convert program_predecessor from RPN ID to RPN alias.
// Used by productDetailsSlice.fetchRPNDetails to convert individual RPN data.
// ============================================================================

/**
 * Builds a map of RPN ID to RPN alias from the minimal hierarchy.
 * Used for converting program_predecessor from rpn_id to rpn_alias (for display).
 * Returns a Map for O(1) lookup performance.
 *
 * @example
 * ```typescript
 * const rpnIdToAliasMap = useAppSelector(selectRpnIdToAliasMap);
 * const alias = rpnIdToAliasMap.get(12345); // Returns "Echo Show Gen 3"
 * ```
 */
export const selectRpnIdToAliasMap = createSelector(
  [selectProductHierarchyMinimalData],
  (minimalData): Map<number, string> => {
    const map = new Map<number, string>();

    minimalData.forEach((productLine) => {
      productLine.children?.forEach((rpn) => {
        map.set(rpn.rpn_id, rpn.rpn_alias);
      });
    });

    return map;
  }
);

/**
 * Builds a map of RPN alias to RPN ID from the minimal hierarchy.
 * Used for converting program_predecessor from rpn_alias to rpn_id (for submission to backend).
 * Returns a Map for O(1) lookup performance.
 *
 * @example
 * ```typescript
 * const rpnAliasToIdMap = useAppSelector(selectRpnAliasToIdMap);
 * const id = rpnAliasToIdMap.get("Echo Show Gen 3"); // Returns 12345
 * ```
 */
export const selectRpnAliasToIdMap = createSelector(
  [selectProductHierarchyMinimalData],
  (minimalData): Map<string, number> => {
    const map = new Map<string, number>();

    minimalData.forEach((productLine) => {
      productLine.children?.forEach((rpn) => {
        map.set(rpn.rpn_alias, rpn.rpn_id);
      });
    });

    return map;
  }
);

// Export the shared conversion utilities for use by components
// The actual implementations are in src/utils/programPredecessorUtils.ts
export { convertProgramPredecessorToId, convertProgramPredecessorValue } from '../../utils/programPredecessorUtils';

// ============================================================================
// Section 4: Product Line Details Selectors (state.productHierarchy.productLineDetails)
//
// These selectors access the flat product line details data from listProductLineDetails API.
// Use these for Product Line Management page and anywhere you need product line info without hierarchy.
// ============================================================================

/** Selects the entire productLineDetails state object (data, isLoading, error, etc.) */
export const selectProductLineDetailsState = (state: RootState) => state.productHierarchy.productLineDetails;

/** Selects the product line details data array (ProductLineDetail[]) */
export const selectProductLineDetailsData = (state: RootState) => state.productHierarchy.productLineDetails.data;

/** Selects whether the product line details are currently loading */
export const selectProductLineDetailsLoading = (state: RootState) =>
  state.productHierarchy.productLineDetails.isLoading;

/** Selects the error message from the product line details fetch, or null */
export const selectProductLineDetailsError = (state: RootState) => state.productHierarchy.productLineDetails.error;

/** Selects whether a fetch for product line details has been attempted */
export const selectProductLineDetailsHasAttemptedFetch = (state: RootState) =>
  state.productHierarchy.productLineDetails.hasAttemptedFetch;

/** Selects whether product line details have been successfully fetched */
export const selectProductLineDetailsHasSuccessfulFetch = (state: RootState) =>
  state.productHierarchy.productLineDetails.hasSuccessfulFetch;

/**
 * Selects sorted product line details applying three-tier sorting:
 * 1. Primary sort: Device suffix first, Accessories last
 * 2. Secondary sort: Product lines with 0 RPNs go to the bottom
 * 3. Tertiary sort: Alphabetical by product_line name
 */
export const selectSortedProductLineDetails = createSelector([selectProductLineDetailsData], (productLines) => {
  const threeTierComparator = createThreeTierSortComparator<ProductLineDetail>(
    (pl) => pl.product_line,
    (pl) => pl.rpn_count ?? 0
  );

  return [...productLines].sort((a, b) => {
    // Handle null/undefined/empty product_line_group values
    const groupA = a.product_line_group || '';
    const groupB = b.product_line_group || '';

    // First compare by product_line_group
    const groupComparison = groupA.localeCompare(groupB);
    if (groupComparison !== 0) {
      return groupComparison;
    }

    // If groups are the same, apply three-tier sorting
    return threeTierComparator(a, b);
  });
});

// ============================================================================
// Section 5: Product Line Groups Selectors (state.productHierarchy.productLineGroups)
//
// These selectors access product line groups data for organizing and categorizing product lines.
// ============================================================================

/**
 * Selector to get product line groups data
 */
export const selectProductLineGroups = (state: RootState) => state.productHierarchy.productLineGroups.data;

/**
 * Selector to get product line groups loading state
 */
export const selectProductLineGroupsLoading = (state: RootState) => state.productHierarchy.productLineGroups.isLoading;

/**
 * Selector to get product line groups as dropdown options
 * Returns options with an empty "None" option first
 *
 * @example
 * ```typescript
 * const groupOptions = useAppSelector(selectProductLineGroupOptions);
 * // [{ label: 'None', value: '' }, { label: 'Group A', value: 'Group A' }, ...]
 * ```
 */
export const selectProductLineGroupOptions = createSelector([selectProductLineGroups], (groups) => [
  { label: 'None', value: '' },
  ...groups.map((group) => ({
    label: group.product_line_group,
    value: group.product_line_group,
  })),
]);
